import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { PaRatePreference } from 'frontend-utils/models/hotel';
import type { RoomType } from 'frontend-utils/models/roomtype';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

type UpdatedSettings = 'rate_type' | 'room_type' | 'persons' | 'mealtype';

export default class WizardsPreferredRateOnboardingService extends Service {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked declare selectedMealTypeId: string;
  @tracked declare selectedPersons: string;
  @tracked declare selectedRateTypeId: string;
  @tracked declare selectedRoomTypeId: RoomType;

  get updatedSettings(): Pick<PaRatePreference, UpdatedSettings> {
    return {
      mealtype: parseInt(this.selectedMealTypeId, 10),
      persons: parseInt(this.selectedPersons, 10),
      rate_type: this.selectedRateTypeId,
      room_type: {
        id: this.selectedRoomTypeId,
        name: this.selectedRoomTypeId,
      },
    };
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    const rateType =
      this.hotelAndMarketService.hotel?.paRatePreference.rate_type;
    if (rateType) {
      this.selectedRateTypeId = rateType;
    }

    const roomType =
      this.hotelAndMarketService.hotel?.paRatePreference.room_type;
    if (roomType) {
      this.selectedRoomTypeId = roomType.id;
    }

    const persons = this.hotelAndMarketService.hotel?.paRatePreference.persons;
    if (persons !== undefined) {
      this.selectedPersons = persons.toString();
    }

    const mealType =
      this.hotelAndMarketService.hotel?.paRatePreference.mealtype;
    if (mealType !== undefined) {
      this.selectedMealTypeId = mealType.toString();
    }
  }

  updateMealType(selectedMealType: string): void {
    this.selectedMealTypeId = selectedMealType;
  }

  updatePersons(selectedPersons: string): void {
    this.selectedPersons = selectedPersons;
  }

  updateRateType(selectedRateType: string): void {
    this.selectedRateTypeId = selectedRateType;
  }

  updateRoomType(selectedRoomType: RoomType): void {
    this.selectedRoomTypeId = selectedRoomType;
  }
}

declare module '@ember/service' {
  interface Registry {
    'wizards/preferred-rate-onboarding': WizardsPreferredRateOnboardingService;
  }
}
